/**
 * Campos obrigatórios da CÓPIA de cenário — regras PURAS (ORG-2, revisão de 11/10/2026).
 * Sem DOM: a tela (`telas/acoes-cenario.ts`) passa `window.faltantes` como argumento.
 *
 * A conferência falha FECHADA: se a função do simulador não existir, der erro ou responder
 * algo sem forma de lista de pares, o resultado é `null` ("não deu para conferir") — nunca
 * `[]`, que quer dizer "completa" e PUBLICARIA a cópia sem ninguém ter conferido. Com `null`,
 * a cópia vira rascunho dela (semente), como quando faltam campos, e o motivo vai ao console.
 */
import type { EntradaMotor } from '../../../packages/dados/src/mapeamento';

/** Para onde vai a cópia feita com o que estava na tela. */
export type DestinoDaCopia = 'publicar' | 'semente';

function ehListaDePares(x: unknown): x is Array<[unknown, string]> {
  return Array.isArray(x) && x.every((p) => Array.isArray(p) && p.length >= 2 && typeof p[1] === 'string');
}

/**
 * Os rótulos dos campos obrigatórios que faltam, pela MESMA função que o "Salvar" do
 * simulador usa (`faltantes(b, L)` devolve pares `[id do campo, rótulo]`). `null` quando não
 * foi possível conferir.
 */
export function faltantesOuNada(fn: unknown, e: EntradaMotor): string[] | null {
  if (typeof fn !== 'function') {
    console.error('[Fincast] Campos obrigatórios: a função "faltantes" do simulador não está disponível — a cópia não será publicada sem conferir.');
    return null;
  }
  let r: unknown;
  try {
    r = (fn as (b: unknown, L: unknown) => unknown)(e.empreendimento, e.lev);
  } catch (err) {
    console.error('[Fincast] Campos obrigatórios: a conferência deu erro — a cópia não será publicada sem conferir.', err);
    return null;
  }
  if (!ehListaDePares(r)) {
    console.error('[Fincast] Campos obrigatórios: resposta inesperada da conferência — a cópia não será publicada sem conferir.', r);
    return null;
  }
  return r.map((p) => p[1]);
}

/** Completa → publica; faltam campos ou não deu para conferir → rascunho da cópia. */
export function destinoDaCopia(faltam: readonly string[] | null): DestinoDaCopia {
  return faltam === null || faltam.length > 0 ? 'semente' : 'publicar';
}

/** O aviso que a tela da cópia mostra quando ela não foi publicada. */
export function avisoDaCopiaNaoPublicada(nome: string, faltam: readonly string[] | null): string {
  const motivo = faltam === null
    ? 'não foi possível conferir os campos obrigatórios'
    : `faltam campos obrigatórios (${faltam.join(', ')})`;
  return `Cópia "${nome}" criada com o que estava na tela. Ela ainda não foi publicada porque ${motivo}: o que você via ficou como alteração não publicada da cópia.`;
}
